/**
 * The v1 Snowflake export universe, mirrored on the frontend for the
 * per-type selection picker. Keep this in sync with the backend
 * `V1_EXPORT_UNIVERSE` in
 * `apps/portfolios/services/snowflake_connection_record_service.py` — the
 * export level is a property of the TYPE and is fixed here, never
 * user-configurable.
 *
 * Display labels are deliberately NOT part of this module: the picker
 * resolves them at render time through the canonical `getTypeLabelByName`
 * helper (schema API first, built-in registry as fallback) so these types
 * read the same here as on every other surface.
 */

export type SnowflakeExportLevel = 'full' | 'org_visible';

export interface SnowflakeSelectableType {
  entityTypeName: string;
  exportLevel: SnowflakeExportLevel;
  carriesPrivacy: boolean;
}

export const SNOWFLAKE_V1_TYPES: readonly SnowflakeSelectableType[] = [
  {
    entityTypeName: 'ahoy_contact',
    exportLevel: 'full',
    carriesPrivacy: false,
  },
  {
    entityTypeName: 'ahoy_company',
    exportLevel: 'full',
    carriesPrivacy: false,
  },
  { entityTypeName: 'ahoy_deal', exportLevel: 'full', carriesPrivacy: false },
  { entityTypeName: 'ahoy_risk', exportLevel: 'full', carriesPrivacy: false },
  {
    entityTypeName: 'ahoy_web_visit',
    exportLevel: 'full',
    carriesPrivacy: false,
  },
  {
    entityTypeName: 'ahoy_web_visitor',
    exportLevel: 'full',
    carriesPrivacy: false,
  },
  {
    entityTypeName: 'ahoy_email_thread',
    exportLevel: 'org_visible',
    carriesPrivacy: true,
  },
  {
    entityTypeName: 'ahoy_meeting',
    exportLevel: 'org_visible',
    carriesPrivacy: true,
  },
  {
    entityTypeName: 'ahoy_meeting_notes',
    exportLevel: 'org_visible',
    carriesPrivacy: true,
  },
  {
    entityTypeName: 'ahoy_note',
    exportLevel: 'org_visible',
    carriesPrivacy: true,
  },
];

/**
 * Which of the v1 Snowflake types actually exist in the org, derived from the org's
 * object-type schema. Returns `undefined` while the schema is still loading (caller should
 * then treat all types as available — the backend 400 is the safety net). An empty Set means
 * the org has NONE of the v1 types enabled.
 */
export function availableV1TypeNames(
  objectTypes: readonly { entity_type_name: string }[] | undefined
): Set<string> | undefined {
  if (objectTypes === undefined) {
    return undefined;
  }
  const existing = new Set(objectTypes.map(t => t.entity_type_name));
  return new Set(
    SNOWFLAKE_V1_TYPES.map(t => t.entityTypeName).filter(name =>
      existing.has(name)
    )
  );
}
